import { useEffect, useState } from "react";
import Icon from "@mdi/react";
import { mdiClose, mdiTrashCanOutline } from "@mdi/js";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import { ReplacementSTOutward } from "../../model/warehouse_outward_model";
import { WarehouseModel } from "../../../../../erp/inventory/models/warehouse_model";
import WarehouseSearchField from "../../../../../erp/inventory/warehouse/warehouse_search_field";
import ItemSearchFieldV2 from "../../../../../erp/inventory/items/item_search_fieldv2";
import RequiredMark from "../../../../new_components/common/required_mark";
import {
  GetItemWAC,
  SubmitDraftReplacementST,
} from "../../warehouse_module_repo";
import DeviceIdsViewer from "../../common-components/DeviceIdsViewer";
import { GetReplacementRequest } from "../../../../../service/repos/support/complaint_repo";
import { Country } from "country-state-city";
import {
  isIndianAddress,
  resolveIndianStateName,
  stateData,
} from "../../../../../utils/states_data";

const COUNTRY_NAMES = Country.getAllCountries().map((c) => c.name);

// Snapshot country can be "INDIA" / blank — show it as the dropdown's "India".
const initialCountry = (raw: unknown) => {
  if (isIndianAddress(raw)) return "India";
  const value = String(raw).trim();
  return (
    COUNTRY_NAMES.find((c) => c.toLowerCase() === value.toLowerCase()) ?? value
  );
};

interface DraftItemRow {
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  itemUnit: string;
  itemHsn: string;
  subcategory: string;
}

interface ManualFaultyItem {
  itemCode: string;
  itemName: string;
  itemUnit: string;
  itemHsn: string;
  subcategory: string;
  unitPrice: number;
  isWacFetched: boolean;
}

type Props = {
  open: boolean;
  onClose: () => void;
  outward: ReplacementSTOutward;
  onSubmitted: () => void;
};

const FillDraftReplacementSTModal: React.FC<Props> = ({
  open,
  onClose,
  outward,
  onSubmitted,
}) => {
  const { showToast } = useToast();
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [blockedDeviceIds, setBlockedDeviceIds] = useState<string[]>([]);
  // Fallback for a Draft whose faulty_items came back empty at raise-time
  // (device->item-code resolution failed, e.g. no linked Order) — same root
  // cause as the blank customer/address. These are raw device ids with no
  // item association yet; the warehouse team picks the faulty item manually.
  const [unresolvedDeviceIds, setUnresolvedDeviceIds] = useState<string[]>([]);
  const [manualFaultyItem, setManualFaultyItem] =
    useState<ManualFaultyItem | null>(null);
  // Faulty items are valued at their working item code's WAC (locked); only
  // items with no WAC take a manually entered price.
  const [faultyWac, setFaultyWac] = useState<Record<string, number>>({});
  const [faultyPrices, setFaultyPrices] = useState<Record<string, number>>({});
  const [items, setItems] = useState<DraftItemRow[]>([]);
  const [fromWarehouse, setFromWarehouse] = useState<WarehouseModel | null>(
    null,
  );
  const [narration, setNarration] = useState(outward.narration ?? "");
  const [submitting, setSubmitting] = useState(false);

  // Stage-1 snapshot can come back sparse when the complaint's devices
  // aren't linked to a fulfilled Order (no auto-resolvable customer/address)
  // — PRD Stage 3 has the warehouse team verify/fix this before submitting.
  const [customerName, setCustomerName] = useState(outward.customer_name ?? "");
  const [contactName, setContactName] = useState(outward.contact_name ?? "");
  const [contactNumber, setContactNumber] = useState(
    outward.contact_number ?? "",
  );
  const [addressLine1, setAddressLine1] = useState(
    (outward.shipping_address as any)?.line1 ?? "",
  );
  const [addressCity, setAddressCity] = useState(
    (outward.shipping_address as any)?.city ?? "",
  );
  const [addressCountry, setAddressCountry] = useState(() =>
    initialCountry((outward.shipping_address as any)?.country),
  );
  // India: pre-select the matching dropdown state; an unrecognised value
  // (e.g. a village typed into State) is cleared so it must be picked.
  const [addressState, setAddressState] = useState(() => {
    const raw = (outward.shipping_address as any)?.state ?? "";
    return isIndianAddress((outward.shipping_address as any)?.country)
      ? (resolveIndianStateName(raw) ?? "")
      : raw;
  });
  const isIndia = addressCountry === "India";
  const [addressPincode, setAddressPincode] = useState(
    (outward.shipping_address as any)?.pincode ?? "",
  );

  useEffect(() => {
    if (!open) return;
    const allDeviceIds = (outward.faulty_items ?? []).flatMap(
      (it) => it.deviceIds ?? [],
    );
    setSelectedDeviceIds(allDeviceIds);
    setUnresolvedDeviceIds([]);
    setManualFaultyItem(null);
    setFaultyPrices({});
    setFaultyWac({});
    const faultyCodes = Array.from(
      new Set((outward.faulty_items ?? []).map((it) => it.inventoryItemCode)),
    );
    Promise.all(
      faultyCodes.map(async (code): Promise<[string, number]> => {
        try {
          const res = await GetItemWAC(code);
          const wac = res?.success ? res?.data?.wac_value : undefined;
          return [code, typeof wac === "number" ? wac : 0];
        } catch {
          return [code, 0];
        }
      }),
    ).then((entries) => setFaultyWac(Object.fromEntries(entries)));

    if (allDeviceIds.length || !outward.replacement_request_id) return;
    const loadUnresolvedDevices = async () => {
      const res = await GetReplacementRequest(outward.replacement_request_id!);
      if (res?.success && Array.isArray(res.data?.device_details)) {
        const ids: string[] = res.data.device_details
          .map((d: any) => d.device_id)
          .filter(Boolean);
        if (ids.length) {
          setUnresolvedDeviceIds(ids);
          setSelectedDeviceIds(ids);
        }
      }
    };
    loadUnresolvedDevices();
  }, [open, outward]);

  if (!open) return null;

  const removeDevice = (deviceId: string) =>
    setSelectedDeviceIds((prev) => prev.filter((d) => d !== deviceId));

  const fetchItemWac = async (
    itemCode: string,
  ): Promise<{ value: number; isFetched: boolean }> => {
    try {
      const res = await GetItemWAC(itemCode);
      if (res?.success && typeof res?.data?.wac_value === "number") {
        const value = res.data.wac_value;
        return { value, isFetched: value > 0 };
      }
      return { value: 0, isFetched: false };
    } catch {
      return { value: 0, isFetched: false };
    }
  };

  const isFaultyPriceLocked = (item: { inventoryItemCode: string }) =>
    (faultyWac[item.inventoryItemCode] ?? 0) > 0;

  const faultyUnitPrice = (item: { inventoryItemCode: string }) =>
    isFaultyPriceLocked(item)
      ? faultyWac[item.inventoryItemCode]
      : (faultyPrices[item.inventoryItemCode] ?? 0);

  const removeItem = (i: number) =>
    setItems((prev) => prev.filter((_, x) => x !== i));

  const handleSubmit = async () => {
    if (!fromWarehouse) {
      showToast({ type: ToastType.warning, text: "Select source warehouse" });
      return;
    }
    if (!items.length) {
      showToast({
        type: ToastType.warning,
        text: "Add at least one replacement item",
      });
      return;
    }
    if (!selectedDeviceIds.length) {
      showToast({
        type: ToastType.warning,
        text: "Select at least one faulty device to replace",
      });
      return;
    }
    if (unresolvedDeviceIds.length > 0 && !manualFaultyItem) {
      showToast({
        type: ToastType.warning,
        text: "Select the faulty item these devices belong to",
      });
      return;
    }
    const selectedAutoFaultyItems = (outward.faulty_items ?? []).filter((it) =>
      (it.deviceIds ?? []).some((d) => selectedDeviceIds.includes(d)),
    );
    if (
      selectedAutoFaultyItems.some((it) => !(faultyUnitPrice(it) > 0)) ||
      (manualFaultyItem && !(manualFaultyItem.unitPrice > 0))
    ) {
      showToast({
        type: ToastType.warning,
        text: "No WAC found for a faulty item. Enter its unit price.",
      });
      return;
    }
    if (!customerName.trim()) {
      showToast({ type: ToastType.warning, text: "Enter customer name" });
      return;
    }
    if (
      !addressLine1.trim() ||
      !addressCity.trim() ||
      !addressState.trim() ||
      !addressCountry.trim()
    ) {
      showToast({
        type: ToastType.warning,
        text: "Fill in the shipping address (line 1, city, state, country)",
      });
      return;
    }
    if (isIndia && !/^\d{6}$/.test(String(addressPincode).trim())) {
      showToast({
        type: ToastType.warning,
        text: "Enter a valid 6-digit pincode",
      });
      return;
    }

    const shipping_address = {
      line1: addressLine1,
      city: addressCity,
      state: addressState,
      country: addressCountry,
      pincode: addressPincode,
    };

    const faulty_items = manualFaultyItem
      ? [
          {
            inventoryItemCode: manualFaultyItem.itemCode,
            itemName: manualFaultyItem.itemName,
            requestedQty: unresolvedDeviceIds.filter((d) =>
              selectedDeviceIds.includes(d),
            ).length,
            itemPrice: manualFaultyItem.unitPrice,
            subCategory: manualFaultyItem.subcategory,
            itemHSN: manualFaultyItem.itemHsn,
            itemUnit: manualFaultyItem.itemUnit,
            deviceIds: unresolvedDeviceIds.filter((d) =>
              selectedDeviceIds.includes(d),
            ),
            gst_applicable: false,
            tax_detail: { igst: 0, cgst: 0, sgst: 0 },
          },
        ]
      : selectedAutoFaultyItems.map((it) => ({
          ...it,
          itemPrice: faultyUnitPrice(it),
        }));

    setSubmitting(true);
    const res = await SubmitDraftReplacementST(outward.id, {
      items: items.map((i) => ({
        inventoryItemCode: i.itemCode,
        itemName: i.itemName,
        requestedQty: i.quantity,
        itemPrice: i.unitPrice,
        subCategory: i.subcategory,
        itemHSN: i.itemHsn,
        itemUnit: i.itemUnit,
        gst_applicable: false,
        tax_detail: { igst: 0, cgst: 0, sgst: 0 },
      })),
      from_warehouse_id: fromWarehouse.id,
      from_warehouse_name: fromWarehouse.name,
      narration,
      selected_device_ids: selectedDeviceIds,
      customer_name: customerName,
      contact_name: contactName,
      contact_number: contactNumber,
      shipping_address,
      faulty_items,
    });
    setSubmitting(false);

    if (res?.success) {
      showToast({
        type: ToastType.success,
        text: "Submitted for HW approval.",
      });
      onSubmitted();
      return;
    }

    if (res?.data?.blocked_device_ids?.length) {
      setBlockedDeviceIds(res.data.blocked_device_ids);
      setSelectedDeviceIds((prev) =>
        prev.filter((d) => !res.data.blocked_device_ids.includes(d)),
      );
    }
    showToast({
      type: ToastType.error,
      text: res?.message || res?.error || "Submit failed.",
    });
  };

  return (
    <div
      id="warehouse-fill-draft-replacement-st-modal"
      className="fixed inset-0 z-50 mt-4 flex items-start justify-center bg-black/40 overflow-y-auto p-4 sm:p-8"
    >
      <div className="relative w-full sm:max-w-2xl max-h-[85vh] bg-white shadow-lg flex flex-col rounded-lg">
        <div className="flex-shrink-0 flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            Fill Draft ST — {outward.id}
          </h2>
          <button
            id="warehouse-fill-draft-replacement-st-modal-close-btn"
            onClick={onClose}
            className="rounded-md p-1 hover:bg-gray-100"
          >
            <Icon path={mdiClose} size={0.9} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">
              Faulty Devices — remove any you don't want to replace
            </h3>

            {blockedDeviceIds.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                Already replaced under a prior delivered ST, removed from
                selection: {blockedDeviceIds.join(", ")}
              </div>
            )}

            {(outward.faulty_items ?? []).map((item, idx) => {
              const selectedForItem = (item.deviceIds ?? []).filter((d) =>
                selectedDeviceIds.includes(d),
              );
              if (!selectedForItem.length) return null;
              return (
                <div
                  key={idx}
                  id={`warehouse-fill-draft-replacement-st-modal-faulty-item-row-${item.inventoryItemCode}`}
                  className="rounded-lg border p-4 space-y-3 bg-orange-50"
                >
                  <div>
                    <p className="font-medium">{item.itemName}</p>
                    <p className="text-sm text-gray-500">
                      {item.inventoryItemCode}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Qty: {selectedForItem.length}
                    </p>
                    {isFaultyPriceLocked(item) && (
                      <p className="text-xs text-gray-600 mt-1">
                        Unit Price (WAC): ₹
                        {faultyUnitPrice(item).toLocaleString()}
                      </p>
                    )}
                  </div>
                  {!isFaultyPriceLocked(item) && (
                    <div>
                      <label className="text-xs font-medium">
                        Unit Price <RequiredMark />
                      </label>
                      <p className="text-xs text-gray-500">
                        No WAC found for this item. Enter the faulty device
                        value.
                      </p>
                      <input
                        id={`warehouse-fill-draft-replacement-st-modal-faulty-item-price-${item.inventoryItemCode}`}
                        type="number"
                        min={0}
                        value={faultyPrices[item.inventoryItemCode] ?? ""}
                        onChange={(e) =>
                          setFaultyPrices((prev) => ({
                            ...prev,
                            [item.inventoryItemCode]: Number(e.target.value),
                          }))
                        }
                        className="w-full h-10 border rounded-md px-3"
                      />
                    </div>
                  )}
                  <DeviceIdsViewer
                    id={`warehouse-fill-draft-replacement-st-modal-faulty-devices-${item.inventoryItemCode}`}
                    title="Faulty Devices"
                    deviceIds={selectedForItem}
                    defaultOpen
                    onRemove={removeDevice}
                  />
                </div>
              );
            })}

            {unresolvedDeviceIds.length > 0 && !manualFaultyItem && (
              <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4 space-y-3">
                <p className="text-sm font-medium">
                  Item code could not be auto-detected for these devices —
                  select the faulty item they belong to <RequiredMark />
                </p>
                <ItemSearchFieldV2
                  id="warehouse-fill-draft-replacement-st-modal-manual-faulty-item-search"
                  disabled={false}
                  value={undefined}
                  subcategory={"Finished Goods"}
                  onSelect={async (item: any) => {
                    if (!item) return;
                    const wac = await fetchItemWac(item.item_code);
                    setManualFaultyItem({
                      itemCode: item.item_code,
                      itemName: item.name,
                      itemUnit: item.unit?.formal_name || "",
                      itemHsn: item.tax_detail?.hsn_sac || "",
                      subcategory: item.subcategory || "",
                      unitPrice: wac.value,
                      isWacFetched: wac.isFetched,
                    });
                  }}
                />
                <DeviceIdsViewer
                  id="warehouse-fill-draft-replacement-st-modal-unresolved-devices"
                  title="Faulty Devices"
                  deviceIds={unresolvedDeviceIds.filter((d) =>
                    selectedDeviceIds.includes(d),
                  )}
                  defaultOpen
                  onRemove={removeDevice}
                />
              </div>
            )}

            {manualFaultyItem && (
              <div
                id={`warehouse-fill-draft-replacement-st-modal-faulty-item-row-${manualFaultyItem.itemCode}`}
                className="rounded-lg border p-4 space-y-3 bg-orange-50"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{manualFaultyItem.itemName}</p>
                    <p className="text-sm text-gray-500">
                      {manualFaultyItem.itemCode}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Qty:{" "}
                      {
                        unresolvedDeviceIds.filter((d) =>
                          selectedDeviceIds.includes(d),
                        ).length
                      }
                    </p>
                  </div>
                  <button
                    id="warehouse-fill-draft-replacement-st-modal-manual-faulty-item-clear-btn"
                    onClick={() => setManualFaultyItem(null)}
                    className="text-red-600"
                  >
                    <Icon path={mdiTrashCanOutline} size={0.8} />
                  </button>
                </div>
                <div>
                  <label className="text-xs font-medium">
                    {manualFaultyItem.isWacFetched ? (
                      "Unit Price (WAC)"
                    ) : (
                      <>
                        Unit Price <RequiredMark />
                      </>
                    )}
                  </label>
                  {!manualFaultyItem.isWacFetched && (
                    <p className="text-xs text-gray-500">
                      No WAC found for this item. Enter the faulty device value.
                    </p>
                  )}
                  <input
                    id="warehouse-fill-draft-replacement-st-modal-manual-faulty-item-price"
                    type="number"
                    min={0}
                    disabled={manualFaultyItem.isWacFetched}
                    value={manualFaultyItem.unitPrice}
                    onChange={(e) =>
                      setManualFaultyItem((prev) =>
                        prev
                          ? { ...prev, unitPrice: Number(e.target.value) }
                          : prev,
                      )
                    }
                    className="w-full h-10 border rounded-md px-3 disabled:bg-gray-100"
                  />
                </div>
                <DeviceIdsViewer
                  id="warehouse-fill-draft-replacement-st-modal-manual-faulty-devices"
                  title="Faulty Devices"
                  deviceIds={unresolvedDeviceIds.filter((d) =>
                    selectedDeviceIds.includes(d),
                  )}
                  defaultOpen
                  onRemove={removeDevice}
                />
              </div>
            )}
          </div>

          <div className="rounded-lg border p-4 space-y-3">
            <p className="text-sm font-medium">
              Customer & Shipping Address <RequiredMark />
            </p>
            <p className="text-xs text-gray-500">
              Verify/complete these — they're pre-filled from the complaint
              where possible, but may need correction.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium">Customer Name</label>
                <input
                  id="warehouse-fill-draft-replacement-st-modal-customer-name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full h-10 border rounded-md px-3"
                />
              </div>
              <div>
                <label className="text-xs font-medium">Contact Number</label>
                <input
                  id="warehouse-fill-draft-replacement-st-modal-contact-number"
                  value={contactNumber}
                  onChange={(e) => setContactNumber(e.target.value)}
                  className="w-full h-10 border rounded-md px-3"
                />
              </div>
              <div>
                <label className="text-xs font-medium">Contact Name</label>
                <input
                  id="warehouse-fill-draft-replacement-st-modal-contact-name"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="w-full h-10 border rounded-md px-3"
                />
              </div>
              <div>
                <label className="text-xs font-medium">
                  Pincode {isIndia && <RequiredMark />}
                </label>
                <input
                  id="warehouse-fill-draft-replacement-st-modal-address-pincode"
                  value={addressPincode}
                  onChange={(e) => setAddressPincode(e.target.value)}
                  className="w-full h-10 border rounded-md px-3"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium">Address Line 1</label>
                <input
                  id="warehouse-fill-draft-replacement-st-modal-address-line1"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  className="w-full h-10 border rounded-md px-3"
                />
              </div>
              <div>
                <label className="text-xs font-medium">City</label>
                <input
                  id="warehouse-fill-draft-replacement-st-modal-address-city"
                  value={addressCity}
                  onChange={(e) => setAddressCity(e.target.value)}
                  className="w-full h-10 border rounded-md px-3"
                />
              </div>
              <div>
                <label className="text-xs font-medium">State</label>
                {isIndia ? (
                  <select
                    id="warehouse-fill-draft-replacement-st-modal-address-state"
                    value={addressState}
                    onChange={(e) => setAddressState(e.target.value)}
                    className="w-full h-10 border rounded-md px-3"
                  >
                    <option value="">Select State</option>
                    {stateData.map((state) => (
                      <option key={state.id} value={state.name}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="warehouse-fill-draft-replacement-st-modal-address-state"
                    value={addressState}
                    onChange={(e) => setAddressState(e.target.value)}
                    className="w-full h-10 border rounded-md px-3"
                  />
                )}
              </div>
              <div>
                <label className="text-xs font-medium">Country</label>
                <select
                  id="warehouse-fill-draft-replacement-st-modal-address-country"
                  value={addressCountry}
                  onChange={(e) => {
                    setAddressCountry(e.target.value);
                    setAddressState(""); // state list differs per country
                  }}
                  className="w-full h-10 border rounded-md px-3"
                >
                  <option value="">Select Country</option>
                  {!COUNTRY_NAMES.includes(addressCountry) &&
                    addressCountry && (
                      <option value={addressCountry}>{addressCountry}</option>
                    )}
                  {COUNTRY_NAMES.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">
              Source Warehouse <RequiredMark />
            </label>
            <WarehouseSearchField
              id="warehouse-fill-draft-replacement-st-modal-warehouse"
              value={fromWarehouse || undefined}
              onSelect={setFromWarehouse}
            />
          </div>

          <div>
            <label className="text-sm font-medium">Narration</label>
            <textarea
              id="warehouse-fill-draft-replacement-st-modal-narration"
              value={narration}
              onChange={(e) => setNarration(e.target.value)}
              rows={2}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Replacement Items</h3>
            {items.map((item, i) => (
              <div
                key={i}
                id={`warehouse-fill-draft-replacement-st-modal-item-row-${item.itemCode}`}
                className="p-4 border rounded-xl bg-gray-50 space-y-3"
              >
                <div className="flex justify-between">
                  <div className="font-medium">
                    {item.itemName} - {item.itemCode}
                  </div>
                  <button
                    id={`warehouse-fill-draft-replacement-st-modal-item-row-${item.itemCode}-remove-btn`}
                    onClick={() => removeItem(i)}
                    className="text-red-600"
                  >
                    <Icon path={mdiTrashCanOutline} size={0.8} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium">Quantity</label>
                    <input
                      type="number"
                      min={1}
                      value={item.quantity}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setItems((prev) => {
                          const updated = [...prev];
                          if (value > 0) updated[i].quantity = value;
                          return updated;
                        });
                      }}
                      className="w-full h-10 border rounded-md px-3"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Unit Price</label>
                    <input
                      type="number"
                      min={0}
                      value={item.unitPrice}
                      onChange={(e) => {
                        const updated = [...items];
                        updated[i].unitPrice = Number(e.target.value);
                        setItems(updated);
                      }}
                      className="w-full h-10 border rounded-md px-3"
                    />
                  </div>
                </div>
              </div>
            ))}

            <ItemSearchFieldV2
              id="warehouse-fill-draft-replacement-st-modal-item-search"
              disabled={false}
              value={undefined}
              subcategory={"Finished Goods"}
              onSelect={async (item: any) => {
                if (!item) return;
                const exists = items.find((i) => i.itemCode === item.item_code);
                if (exists) return;

                const wac = await fetchItemWac(item.item_code);
                setItems((prev) => [
                  ...prev,
                  {
                    itemCode: item.item_code,
                    itemName: item.name,
                    quantity: selectedDeviceIds.length || 1,
                    unitPrice: wac.value,
                    itemUnit: item.unit?.formal_name || "",
                    itemHsn: item.tax_detail?.hsn_sac || "",
                    subcategory: item.subcategory || "",
                  },
                ]);
              }}
            />
          </div>
        </div>

        <div className="border-t px-6 py-4 flex justify-end gap-3">
          <button
            id="warehouse-fill-draft-replacement-st-modal-cancel-btn"
            onClick={onClose}
            className="border px-6 py-2 rounded-lg"
          >
            Cancel
          </button>
          <button
            id="warehouse-fill-draft-replacement-st-modal-submit-btn"
            disabled={submitting}
            onClick={handleSubmit}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg disabled:opacity-50"
          >
            {submitting ? "Submitting..." : "Submit for HW Approval"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FillDraftReplacementSTModal;
